<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  autoConnect: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['submit', 'close']);

const { t } = useI18n();

const dialogRef = ref(null);
const rustdeskId = ref('');
const label = ref('');
const rustdeskPassword = ref('');

const isIdValid = computed(() => /^\d{9}$/.test(rustdeskId.value));
const isSubmitDisabled = computed(() => !isIdValid.value || props.isLoading);

const onIdInput = value => {
  rustdeskId.value = String(value ?? '')
    .replace(/\D+/g, '')
    .slice(0, 9);
};

const idMessage = computed(() => {
  if (rustdeskId.value.length > 0 && !isIdValid.value) {
    return t('CONVERSATION.REMOTE_SUPPORT.ID_INVALID');
  }
  return t('CONVERSATION.REMOTE_SUPPORT.ID_HELP');
});

const idMessageType = computed(() =>
  rustdeskId.value.length > 0 && !isIdValid.value ? 'error' : 'info'
);

const confirmLabel = computed(() =>
  props.autoConnect
    ? t('CONVERSATION.REMOTE_SUPPORT.SAVE_AND_CONNECT')
    : t('CONVERSATION.REMOTE_SUPPORT.SAVE')
);

const handleConfirm = () => {
  if (isSubmitDisabled.value) return;
  emit('submit', {
    rustdeskId: rustdeskId.value.trim(),
    label: label.value.trim(),
    rustdeskPassword: rustdeskPassword.value.trim(),
  });
};

const handleClose = () => {
  emit('close');
};

onMounted(() => {
  dialogRef.value?.open();
});

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="t('CONVERSATION.REMOTE_SUPPORT.ADD_ID_TITLE')"
    :confirm-button-label="confirmLabel"
    :cancel-button-label="t('DIALOG.BUTTONS.CANCEL')"
    :disable-confirm-button="isSubmitDisabled"
    :is-loading="isLoading"
    width="md"
    @confirm="handleConfirm"
    @close="handleClose"
  >
    <div class="flex flex-col gap-4">
      <Input
        :model-value="rustdeskId"
        :label="t('CONVERSATION.REMOTE_SUPPORT.ID_LABEL')"
        type="text"
        inputmode="numeric"
        pattern="\d{9}"
        maxlength="9"
        :placeholder="t('CONVERSATION.REMOTE_SUPPORT.ID_PLACEHOLDER')"
        :message="idMessage"
        :message-type="idMessageType"
        autofocus
        @update:model-value="onIdInput"
      />
      <Input
        v-model="label"
        :label="t('CONVERSATION.REMOTE_SUPPORT.LABEL_LABEL')"
        type="text"
        :placeholder="t('CONVERSATION.REMOTE_SUPPORT.LABEL_PLACEHOLDER')"
      />
      <Input
        v-model="rustdeskPassword"
        :label="t('CONVERSATION.REMOTE_SUPPORT.PASSWORD_LABEL')"
        type="text"
        :placeholder="t('CONVERSATION.REMOTE_SUPPORT.PASSWORD_PLACEHOLDER')"
        :message="t('CONVERSATION.REMOTE_SUPPORT.PASSWORD_HELP')"
        message-type="info"
      />
    </div>
  </Dialog>
</template>
